import { useState, useEffect } from 'react';
import { Trash2, Users, UserCheck, Briefcase, AlertTriangle } from 'lucide-react';

// Base URL for API
const API_BASE_URL = 'https://dev-projecthub.vjstartup.com/ph-be';

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('students');
  const [students, setStudents] = useState([]);
  const [faculty, setFaculty] = useState([]);
  const [studentProjects, setStudentProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === 'students') {
      fetchStudents();
    } else if (activeTab === 'faculty') {
      fetchFaculty();
    } else if (activeTab === 'projects') {
      fetchStudentProjects();
    }
  }, [activeTab]);

  // Fetch students
  const fetchStudents = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/students`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
     
      const data = await response.json();
      setStudents(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch faculty
  const fetchFaculty = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/faculty`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch faculty');
      const data = await response.json();
      setFaculty(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch student projects
  const fetchStudentProjects = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/student-projects`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch student projects');
      const data = await response.json();
      setStudentProjects(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete student
  const deleteStudent = async (studentId) => {
    if (!window.confirm('Are you sure you want to delete this student?')) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/students/${studentId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to delete student');
      
      // Update students list
      setStudents(students.filter(student => student.id !== studentId));
      showSuccessMessage('Student deleted successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete faculty
  const deleteFaculty = async (rollId) => {
    if (!window.confirm('Are you sure you want to delete this faculty member?')) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/faculty/${rollId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to delete faculty');
      
      // Update faculty list
      setFaculty(faculty.filter(f => f.roll_id !== rollId));
      showSuccessMessage('Faculty deleted successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete student project assignment
  const deleteStudentProject = async (facultyId, projectId, rollId) => {
    if (!window.confirm('Are you sure you want to delete this project assignment?')) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/student-projects/${facultyId}/${projectId}/${rollId}`, 
        { 
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) throw new Error('Failed to delete project assignment');
      
      // Update project assignments list
      setStudentProjects(studentProjects.filter(
        project => !(project.faculty_id === facultyId && 
                     project.project_id === projectId && 
                     project.student_id === rollId)
      ));
      showSuccessMessage('Project assignment deleted successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Show success message with timeout
  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-blue-600 text-white p-4">
        <div className="container mx-auto">
          <h1 className="text-2xl font-bold">Project Hub - Admin Dashboard</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto p-4">
        {/* Navigation Tabs */}
        <div className="flex mb-6 border-b">
          <button 
            className={`px-4 py-2 font-medium ${activeTab === 'students' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`}
            onClick={() => setActiveTab('students')}
          >
            <div className="flex items-center gap-2">
              <Users size={20} /> Students
            </div>
          </button>
          <button 
            className={`px-4 py-2 font-medium ${activeTab === 'faculty' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`}
            onClick={() => setActiveTab('faculty')}
          >
            <div className="flex items-center gap-2">
              <UserCheck size={20} /> Faculty
            </div>
          </button>
          <button 
            className={`px-4 py-2 font-medium ${activeTab === 'projects' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`}
            onClick={() => setActiveTab('projects')}
          >
            <div className="flex items-center gap-2">
              <Briefcase size={20} /> Project Assignments
            </div>
          </button>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded flex items-center gap-2">
            <AlertTriangle size={20} />
            {error}
          </div>
        )}

        {/* Loading Indicator */}
        {loading && (
          <div className="flex justify-center my-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Students Tab */}
        {activeTab === 'students' && !loading && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Manage Students</h2>
            {students.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded shadow">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="py-3 px-4 text-left">ID</th>
                      <th className="py-3 px-4 text-left">Name</th>
                      <th className="py-3 px-4 text-left">Email</th>
                      <th className="py-3 px-4 text-left">Department</th>
                      <th className="py-3 px-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map(student => (
                      <tr key={student.id} className="border-t hover:bg-gray-50">
                        <td className="py-3 px-4">{student.id}</td>
                        <td className="py-3 px-4">{student.name}</td>
                        <td className="py-3 px-4">{student.email}</td>
                        <td className="py-3 px-4">{student.department}</td>
                        <td className="py-3 px-4 text-center">
                          <button 
                            onClick={() => deleteStudent(student.id)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                            title="Delete Student"
                          >
                            <Trash2 size={20} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center p-8 bg-white rounded shadow">
                <p className="text-gray-500">No students found</p>
              </div>
            )}
          </div>
        )}

        {/* Faculty Tab */}
        {activeTab === 'faculty' && !loading && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Manage Faculty</h2>
            {faculty.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded shadow">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="py-3 px-4 text-left">Roll ID</th>
                      <th className="py-3 px-4 text-left">Name</th>
                      <th className="py-3 px-4 text-left">Email</th>
                      <th className="py-3 px-4 text-left">Department</th>
                      <th className="py-3 px-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {faculty.map(fac => (
                      <tr key={fac.roll_id} className="border-t hover:bg-gray-50">
                        <td className="py-3 px-4">{fac.roll_id}</td>
                        <td className="py-3 px-4">{fac.name}</td>
                        <td className="py-3 px-4">{fac.email}</td>
                        <td className="py-3 px-4">{fac.department}</td>
                        <td className="py-3 px-4 text-center">
                          <button 
                            onClick={() => deleteFaculty(fac.roll_id)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                            title="Delete Faculty"
                          >
                            <Trash2 size={20} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center p-8 bg-white rounded shadow">
                <p className="text-gray-500">No faculty members found</p>
              </div>
            )}
          </div>
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && !loading && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Manage Project Assignments</h2>
            {studentProjects.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded shadow">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="py-3 px-4 text-left">Faculty ID</th>
                      <th className="py-3 px-4 text-left">Project ID</th>
                      <th className="py-3 px-4 text-left">Student ID</th>
                      <th className="py-3 px-4 text-left">Student Name</th>
                      <th className="py-3 px-4 text-left">Department</th>
                      <th className="py-3 px-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentProjects.map((project, index) => (
                      <tr key={index} className="border-t hover:bg-gray-50">
                        <td className="py-3 px-4">{project.faculty_id}</td>
                        <td className="py-3 px-4">{project.project_id}</td>
                        <td className="py-3 px-4">{project.student_id}</td>
                        <td className="py-3 px-4">{project.student_name}</td>
                        <td className="py-3 px-4">{project.department}</td>
                        <td className="py-3 px-4 text-center">
                          <button 
                            onClick={() => deleteStudentProject(
                              project.faculty_id, 
                              project.project_id, 
                              project.student_id
                            )}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                            title="Delete Project Assignment"
                          >
                            <Trash2 size={20} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center p-8 bg-white rounded shadow">
                <p className="text-gray-500">No project assignments found</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-200 p-4 mt-8">
        <div className="container mx-auto text-center text-gray-600">
          <p>© 2025 Project Hub Admin Dashboard</p>
        </div>
      </footer>
    </div>
  );
}
